import React, { useEffect, useRef, useState, ReactElement, ReactNode } from 'react';

import { cx } from '@travel/utils';

import styles from './sticky.module.scss';

type Props = {
  /** Custom style for wrapper */
  className?: string;
  /** Custom style for wrapper when it is on floating state */
  floatingStateClassName?: string;
  /** Container element */
  root?: Element | null;
  /** Content */
  children: ReactElement | ReactNode;
  /** rootMargin to pass to Intersection Observer ex.) '0px 0px -${child height} 0px' */
  rootMargin?: string;
  /** Number which indicate at what percentage of the target's visibility the observer's callback should be executed */
  threshold?: number;
  /** Flag to define which default floating state should be */
  isFloatingByDefault?: boolean;
  /** Value to define sticky position */
  position?: 'bottom' | 'top';
  /** Custom top value when floating */
  positionTop?: number;
  /** Value to define custom styling */
  /** children height for dummy height when floating */
  /** NOTE: necessary when background is not set on parent component */
  childrenHeight?: number;
  /** String of sticky wrapper style */
  customStyle?: 'transparent' | 'default';
  /** Callback to be called when floating state changed */
  onChange?: (isFloating: boolean) => void;
};

function Sticky(props: Props) {
  const {
    className,
    floatingStateClassName,
    children,
    onChange,
    root = null,
    threshold = 0,
    rootMargin = '0px 0px 0px 0px',
    position = 'bottom',
    positionTop,
    childrenHeight = 0,
    customStyle = 'default',
    isFloatingByDefault = true,
    ...rest
  } = props;

  const [isFloating, setIsFloating] = useState(isFloatingByDefault);
  const target = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const options = {
      root,
      rootMargin,
      threshold,
    };

    const handleIntersect = (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.intersectionRatio > threshold) {
        setIsFloating(false);
        onChange?.(false);
      } else {
        setIsFloating(true);
        onChange?.(true);
      }
    };

    const observer = new IntersectionObserver(handleIntersect, options);
    observer.observe(target.current as Element);

    return function cleanup() {
      observer.disconnect();
    };
  }, [onChange, root, rootMargin, target, threshold]);

  return (
    <>
      <div className={className} ref={target} data-testid="sticky-wrapper" {...rest}>
        <div
          className={cx(
            isFloating ? cx(styles.floating, floatingStateClassName) : styles.static,
            styles[position],
            styles[customStyle],
          )}
          style={positionTop ? { top: `${positionTop}px` } : {}}
        >
          {children}
        </div>
      </div>
      {isFloating && <div style={{ height: childrenHeight }} />}
    </>
  );
}

export default Sticky;
